import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useOrg } from '../auth/OrgContext';
import { useProjects } from '../auth/ProjectContext';

export interface PMTask {
  id: string;
  name: string;
  description?: string;
  dueDate?: string;
  status?: string;
}

export interface PMProject {
  id: string;
  name?: string;
  project_type?: string;
  tasks: PMTask[];
}

interface PMContextType {
  projects: PMProject[];
  createProject: (name: string, type?: string) => Promise<PMProject | null>;
  updateProject: (id: string, updates: Partial<PMProject>) => void;
  addTask: (projectId: string, task: Omit<PMTask, 'id'>) => Promise<void>;
  updateTask: (projectId: string, taskId: string, updates: Partial<PMTask>) => Promise<void>;
}

const PMContext = createContext<PMContextType | undefined>(undefined);

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '') + '/api';

export const ProjectDataProvider = ({ children }: { children: React.ReactNode }) => {
  const { idToken } = useAuth();
  const { currentOrg } = useOrg();
  const { currentProject, refreshProjects } = useProjects();
  const [projects, setProjects] = useState<PMProject[]>([]);

  useEffect(() => {
    if (!idToken || !currentOrg || !currentProject) return;
    fetch(`${API_BASE}/orgs/${currentOrg.id}/projects/${currentProject.id}/tasks`, {
      headers: { Authorization: `Bearer ${idToken}` },
      credentials: 'include'
    })
      .then(res => res.ok ? res.json() : [])
      .then((tasks: PMTask[]) => {
        setProjects(prev => {
          const others = prev.filter(p => p.id !== currentProject.id);
          return [...others, { id: currentProject.id, name: currentProject.name, tasks }];
        });
      })
      .catch(() => { /* ignore */ });
  }, [idToken, currentOrg, currentProject, setProjects]);

  const createProject = async (name: string, type?: string): Promise<PMProject | null> => {
    if (!idToken || !currentOrg) return null;
    const res = await fetch(`${API_BASE}/orgs/${currentOrg.id}/projects`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name })
    });
    if (!res.ok) return null;
    const data = await res.json();
    const proj: PMProject = { id: data.id, name: data.name, project_type: type, tasks: [] };
    setProjects(prev => [...prev, proj]);
    refreshProjects();
    return proj;
  };

  const updateProject = (id: string, updates: Partial<PMProject>) => {
    setProjects(prev => prev.map(p => (p.id === id ? { ...p, ...updates } : p)));
  };

  const addTask = async (projectId: string, task: Omit<PMTask, 'id'>) => {
    if (!idToken || !currentOrg) return;
    try {
      const res = await fetch(`${API_BASE}/orgs/${currentOrg.id}/projects/${projectId}/tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(task)
      });
      if (!res.ok) {
        const errorText = await res.text();
        console.error(`Failed to add task: ${res.status} - ${errorText}`);
        throw new Error(`Failed to add task: ${res.statusText}`);
      }
      const data = await res.json();
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, tasks: [...p.tasks, data] } : p));
    } catch (error) {
      console.error('An error occurred while adding a task:', error);
      throw error; // Optionally re-throw the error to propagate it to the caller
    }
  };

  const updateTask = async (projectId: string, taskId: string, updates: Partial<PMTask>) => {
    if (!idToken || !currentOrg) return;
    await fetch(`${API_BASE}/orgs/${currentOrg.id}/projects/${projectId}/tasks/${taskId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(updates)
    });
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, tasks: p.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t) } : p));
  };

  return (
    <PMContext.Provider value={{ projects, createProject, updateProject, addTask, updateTask }}>
      {children}
    </PMContext.Provider>
  );
};

export const useProjectData = () => {
  const ctx = useContext(PMContext);
  if (!ctx) throw new Error('useProjectData must be inside ProjectDataProvider');
  return ctx;
};
